import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { APP_ROUTES } from '../../config/routes';
import { useMFAStatus } from '../../hooks/useMFAStatus';

/**
 * Route guard that traps unenrolled users at /setup-mfa once enforcement
 * is active.
 *
 * Lets through:
 *  - Users who are enrolled (status.enrolled === true).
 *  - Already-on-/setup-mfa or /mfa-was-reset — no redirect loop.
 *  - All users when MFA enforcement is off — the status endpoint
 *    reports actual DB enrollment regardless of enforcement state, so
 *    this gate must consult enforcementActive directly (the
 *    SessionService bypass only affects the login-flow JWT claim, not
 *    /auth/mfa/status).
 *  - During the initial load before status resolves, render children to
 *    avoid a flash-of-redirect (the layout shows its own skeleton).
 */
export const RequireMFAEnrolled: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { status, isLoading, enrolled, enforcementActive } = useMFAStatus();

  // Pages that are themselves part of the enrollment flow bypass the
  // guard so the trap can resolve without looping.
  const onAllowedPath =
    location.pathname === APP_ROUTES.SETUP_MFA ||
    location.pathname === APP_ROUTES.MFA_WAS_RESET ||
    location.pathname.startsWith('/logout');

  if (onAllowedPath) {
    return <>{children}</>;
  }

  // While the first status request is in flight we have nothing to gate
  // on yet. Render children — at worst the user briefly sees a page they
  // shouldn't, then gets redirected on the next effect tick. This is
  // the same behaviour the org-context guard uses.
  if (isLoading || status === undefined) {
    return <>{children}</>;
  }

  if (enforcementActive && !enrolled) {
    return <Navigate to={APP_ROUTES.SETUP_MFA} replace />;
  }

  return <>{children}</>;
};
